import { TrashSimple } from "@phosphor-icons/react"
import { useState } from "react"
import { toast } from "react-toastify"

import { Button } from "~/components/reusable/Button"
import {
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import { trackEvent } from "~/hooks/useTrackingService"
import { type Auth0User } from "~/server/api/routers/auth0"
import { api } from "~/utils/api"
import { isProduction } from "~/utils/environment"
import { isHakioStaffUser } from "~/utils/users"

type Props = {
  user: Auth0User
  organizationId?: string
}

export const RemoveUserDialog = ({ user, organizationId }: Props) => {
  const utils = api.useUtils()
  const [open, setOpen] = useState(false)

  // Outside production only Hakio staff accounts may be removed.
  const blockedTarget = !isProduction() && !isHakioStaffUser(user)

  const removeUser = api.auth0.removeUser.useMutation({
    onError: async () => {
      toast.error("Failed to remove user")
    },
    onSuccess: async () => {
      trackEvent("User Removed")
      toast.success(user.name + " removed")
      void utils.auth0.getUsers.invalidate()
      void utils.user.getOrgUsers.invalidate()
      setOpen(false)
    },
  })

  return (
    <DialogRoot open={open} onOpenChange={setOpen}>
      <DialogTrigger
        requires="administer"
        disabled={blockedTarget}
        className="cursor-pointer disabled:cursor-not-allowed disabled:text-fg-disabled"
      >
        <TrashSimple />
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Are you absolutely sure?</DialogTitle>
        </DialogHeader>
        <div className="text-sm">
          You are about to remove &quot;{user.name}&quot;. They will no longer have access to your
          organization.
        </div>
        <DialogFooter className="flex justify-between">
          <DialogClose>
            <Button asChild variant="transparent">
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="danger"
            requires="administer"
            loading={removeUser.isPending}
            onClick={() => removeUser.mutate({ userIds: [user.user_id], organizationId })}
          >
            Remove user
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
